import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  clearSelection,
  describeSelection,
  selectAllMatching,
  selectionCount,
  type SelectionState,
} from '../../lib/selection';
import { formatNumber } from '../../lib/format';
import styles from './BulkActionBar.module.css';

// 06-qol §3: appears on selection, shows the count and the available actions, and
// offers a clear deselect.

interface BulkActionBarProps {
  readonly selection: SelectionState;
  readonly total: number | undefined;
  /** Only once the visible page is fully selected and more lies behind the filter. */
  readonly canSelectAllMatching: boolean;
  readonly onSelectionChange: (selection: SelectionState) => void;
  readonly children?: ReactNode;
}

export function BulkActionBar({
  selection,
  total,
  canSelectAllMatching,
  onSelectionChange,
  children,
}: BulkActionBarProps): ReactNode {
  const count = selectionCount(selection, total);

  return (
    <div className={styles.bar} role="region" aria-label="Bulk actions">
      <span className={styles.count}>
        <span className={styles.countNumber}>{count === undefined ? '—' : formatNumber(count)}</span>
        {count === 1 ? 'row' : 'rows'}
        <span className="sr-only">{describeSelection(selection, total)}</span>
      </span>

      {/* The affordance that makes "select all matching" genuinely distinct from
          "select all on this page" — offered only when the two would differ. */}
      {canSelectAllMatching && total !== undefined && (
        <button
          type="button"
          className={styles.selectAll}
          onClick={() => onSelectionChange(selectAllMatching(selection))}
        >
          Select all {formatNumber(total)} matching
        </button>
      )}

      {children !== undefined && (
        <>
          <span className={styles.divider} aria-hidden />
          <span className={styles.actions}>{children}</span>
        </>
      )}

      <button
        type="button"
        className={styles.clear}
        onClick={() => onSelectionChange(clearSelection())}
        aria-label="Clear selection"
      >
        <X size={16} aria-hidden />
      </button>
    </div>
  );
}
